import React, { useMemo } from "react";
// InlineOrModal: a plain ReactModal, except inside the quote workspace (no pop-ups) where it opens in place.
import InlineOrModal from "../../../../core/components/InlineOrModal";
import axios from "axios";
import { isNaN, parseInt } from "lodash";
import { requestTypes } from "../filter/constants";
import { Button, ButtonGroup, Icon, IconButton } from "../../../../components/ui";

const NewPopup = ({
  source,
  checkedData,
  activeItem,
  isOpen,
  setIsOpen,
  matchItem,
  isNonMatch,
  setIsFetched,
  fetchNewData,
  formData,
  requestType,
}) => {
  const scenario = useMemo(() => {
    return (
      (checkedData.reference_exists ? 1 : 0) +
      (checkedData.model_exists ? 1 : 0) +
      (checkedData.brand_exists ? 1 : 0)
    );
  }, [checkedData]);

  // Same-origin proxies on the Laravel backend — see useTradeImage.js
  // for rationale (production CORS block on duckdns).
  const whatsappUrl = "/api/integrations/daowhatsapp";
  const marketUrl = "/api/integrations/daomarketplace";

  const loadNewData = async () => {
    try {
      const response = await axios.post(`${marketUrl}/product/add-product`, {
        scraped_data: {
          ...(source === "marketplace"
            ? { temp_id: matchItem.scraped_data.temp_id ?? "" }
            : {}),
          brand: checkedData.brand,
          model: checkedData.model,
          reference_number: checkedData.reference_number,
          source,
        },
        approved_data: {
          brand: activeItem.brand,
          model: activeItem.model,
          reference_number: activeItem.reference_number,
          values: {
            title: activeItem.title || "",
            case_diameter: activeItem.case_diameter || "",
            movement_info: activeItem.movement_details || "",
            case_glass: activeItem.case_glass || "",
            case_back: activeItem.case_back || "",
            case_water: activeItem.case_water || "",
            dial_color: activeItem.dial_color || "",
            dial_indexes: activeItem.dial_indexes || "",
            case_material: activeItem.case_material || "",
            production_year: activeItem.production_year || "",
            movement_reference: activeItem.movement_reference || "",
          },
          images: activeItem.image_url ? [activeItem.image_url] : [""],
        },
      });

      if (source === "whatsapp" && requestType === requestTypes[0].id) {
        await axios.post(`${whatsappUrl}/submit_price_to_dao`, {
          phone: matchItem.sender_phone,
          sender_name: matchItem.sender_name,
          message_id: String(matchItem.message_id),
          product_id: response.data.product_id.data.id,
          currency: formData.currency,
          price: isNaN(parseInt(formData.price)) ? 0 : parseInt(formData.price),
          condition: Array.isArray(formData.condition)
            ? formData.condition
            : [formData.condition],
          box: formData.deliverySet,
          location: formData.location,
          year_month: formData.month
            ? formData.year + " " + formData.month
            : formData.year + "",
          price_date: matchItem.message_date,
          is_non_match: isNonMatch,
        });
      }
      setIsFetched(false);
      setIsOpen(false);
      fetchNewData(isNonMatch);
    } catch (error) {
      console.error("Error adding product:", error);
    }
  };

  const assignData = async () => {
    try {
      const response = await axios.post(`${marketUrl}/product/assign-product`, {
        scraped_data: {
          ...(source === "marketplace"
            ? { temp_id: matchItem.scraped_data.temp_id ?? "" }
            : {}),
          brand: checkedData.brand,
          model: checkedData.model,
          reference_number: checkedData.reference_number,
          source,
        },
        approved_data: {
          brand: checkedData.brand,
          brand_id: checkedData.brand_id,
          model: checkedData.model,
          model_id: checkedData.model_id,
          reference_number: checkedData.reference_number,
          reference_id: checkedData.reference_id,
        },
      });

      if (source === "whatsapp" && requestType === requestTypes[0].id) {
        await axios.post(`${whatsappUrl}/submit_price_to_dao`, {
          phone: matchItem.sender_phone,
          sender_name: matchItem.sender_name,
          message_id: String(matchItem.message_id),
          product_id: checkedData.reference_id,
          currency: formData.currency,
          price: isNaN(parseInt(formData.price)) ? 0 : parseInt(formData.price),
          condition: Array.isArray(formData.condition)
            ? formData.condition
            : [formData.condition],
          box: formData.deliverySet,
          location: formData.location,
          year_month: formData.month
            ? formData.year + " " + formData.month
            : formData.year + "",
          price_date: matchItem.message_date,
          is_non_match: isNonMatch,
        });
      }
      setIsFetched(false);
      setIsOpen(false);
      fetchNewData(isNonMatch);
    } catch (error) {
      console.error("Error assigning product:", error);
    }
  };

  return (
    <InlineOrModal
      inlineClose={false}
      isOpen={isOpen}
      onRequestClose={() => setIsOpen(false)}
      className="bg-white text-black shadow-lg border-0 rounded-4 p-4"
      style={{
        content: {
          position: "absolute",
          inset: "37% 30%",
          WebkitOverflowScrolling: "touch",
          borderRadius: "4px",
          outline: "none",
          padding: "20px",
        },
      }}
    >
      <div className="modal-content p-4">
        <div className="modal-header border-0 pb-0">
          <h4 className="modal-title w-100 text-center fw-bold">
            Product status
          </h4>
          <IconButton
            variant="ghost"
            label="Close product status"
            data-btn="extraction-status-close"
            onClick={() => setIsOpen(false)}
          >
            <Icon name="X" />
          </IconButton>
        </div>

        <div className="modal-body text-center bg-grey text-black">
          {scenario === 3 && (
            <div className="alert border-dark rounded-3 shadow-sm d-flex align-items-center">
              <p className="mb-0 ms-2 fw-semibold">
                Reference number already exists in DAO.
              </p>
            </div>
          )}

          {scenario === 2 && (
            <div className="alert border-dark rounded-3 shadow-sm d-flex align-items-center">
              <p className="mb-0 ms-2 fw-semibold">
                Model and Brand exist, but Reference number does not exist in
                DAO.
              </p>
            </div>
          )}

          {scenario === 1 && (
            <div className="alert border-dark rounded-3 shadow-sm d-flex align-items-center">
              <p className="mb-0 ms-2 fw-semibold">
                Brand exists, but Model and Reference number do not exist in
                DAO.
              </p>
            </div>
          )}

          {scenario === 0 && (
            <div className="alert border-dark rounded-3 shadow-sm d-flex align-items-center">
              <p className="mb-0 ms-2 fw-semibold">
                Brand, Model, and Reference number do not exist in DAO.
              </p>
            </div>
          )}
        </div>

        <ButtonGroup align="center" className="modal-footer border-0">
          <Button
            variant="secondary"
            data-btn="extraction-status-cancel"
            onClick={() => setIsOpen(false)}
          >
            Close
          </Button>

          {scenario === 3 ? (
            <Button
              variant="primary"
              data-btn="extraction-status-assign"
              onClick={assignData}
            >
              Assign
            </Button>
          ) : (
            <Button
              variant="primary"
              data-btn="extraction-status-add"
              onClick={loadNewData}
            >
              Add to DB
            </Button>
          )}
        </ButtonGroup>
      </div>
    </InlineOrModal>
  );
};

export default NewPopup;
